import { inject, Injectable } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';

import { ExtendedLink } from '../business/extended-link/extendedLink';
import { Link } from '../business/link/link';
import { LinkService } from './link.service';

export const actionBrowser = '?webview_action=browser';

@Injectable({ providedIn: 'root' })
export class ExtendedLinkService {
    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly linkService = inject(LinkService);

    public getLink(extendedLink: ExtendedLink): string {
        return this.linkService.getLinkSync(extendedLink.link);
    }

    public openLink(extendedLink: ExtendedLink): void {
        const updatedRelation = this.handleQueryParams(extendedLink);
        this.linkService.openLink(
            updatedRelation.link,
            updatedRelation.trackingData,
            updatedRelation.params,
            updatedRelation.navigationExtras,
            updatedRelation.currentTab,
        );
    }

    public handleQueryParams(extendedLink: ExtendedLink): ExtendedLink {
        // Create a deep copy of the relation to avoid modifying the original
        const updatedExtendedLink = new ExtendedLink({
            ...extendedLink,
            navigationExtras: extendedLink.navigationExtras
                ? { ...extendedLink.navigationExtras, queryParams: { ...extendedLink.navigationExtras.queryParams } }
                : {},
        });

        // Initialize queryParams if not present
        updatedExtendedLink.navigationExtras.queryParams ??= {};

        // Get query params from the current route, only if urlQueryParams doesn't contain return_url
        let routeParams = { ...this.activatedRoute?.snapshot?.queryParams };

        // Get the string URL from the Relation.link
        const linkString = this.linkService.getLinkSync(updatedExtendedLink.link);
        // Extract query params from the string link
        const urlQueryParams = this.extractQueryParamsFromLink(linkString);
        // Extract return_url query Params prioritize by Relation.navigationExtras and next from Relation.link and finally from routeParams
        const return_url =
            routeParams['return_url'] ??
            urlQueryParams['return_url'] ??
            updatedExtendedLink.navigationExtras?.queryParams?.['return_url'];
        if (return_url) {
            // If return_url is from routeParams, preserve it while clearing other params
            if (routeParams['return_url']) {
                const preservedReturnUrl = routeParams['return_url'];
                routeParams = { return_url: preservedReturnUrl };
                delete updatedExtendedLink.navigationExtras.queryParams['return_url'];
                delete urlQueryParams['return_url'];
            } else {
                routeParams = {};
            }
        }

        // Find backPage from either URL query params or from existing navigationExtras
        const backPageParam =
            routeParams['backPage'] ||
            urlQueryParams['backPage'] ||
            extendedLink.navigationExtras?.queryParams?.['backPage'];
        // Find backRoute from either URL query params or from existing navigationExtras
        const backRouteParam =
            routeParams['backRoute'] ||
            urlQueryParams['backRoute'] ||
            extendedLink.navigationExtras?.queryParams?.['backRoute'];

        // Clean the link by removing query parameters using the already obtained string link
        const cleanLink = this.cleanQueryParamsFromRelationLink(linkString);

        // Update the link object to use the cleaned URL
        updatedExtendedLink.link = this.updateLinkWithCleanUrl(updatedExtendedLink.link, cleanLink);

        // Add parameters based on flags
        if (updatedExtendedLink.shouldKeepQueryParams) {
            // Add route params and existing params
            updatedExtendedLink.navigationExtras.queryParams = {
                ...updatedExtendedLink.navigationExtras.queryParams,
                ...urlQueryParams,
                ...routeParams, // Add URL params with the highest priority;
            };
        } else {
            // Clear all existing params when shouldKeepQueryParams is false
            updatedExtendedLink.navigationExtras.queryParams = {};
        }

        // Handle backPage parameter separately based on shouldKeepBackPage flag
        if (updatedExtendedLink.shouldKeepBackPage !== false && backPageParam) {
            // Add backPage to navigationExtras if shouldKeepBackPage is true or undefined
            updatedExtendedLink.navigationExtras.queryParams['backPage'] = backPageParam;
        } else if (updatedExtendedLink.shouldKeepBackPage === false) {
            // Remove backPage from navigationExtras if shouldKeepBackPage is explicitly false
            delete updatedExtendedLink.navigationExtras.queryParams['backPage'];
        }

        // Handle backRoute parameter separately based on shouldKeepBackRoute flag
        if (updatedExtendedLink.shouldKeepBackRoute !== false && backRouteParam) {
            // Add backRoute to navigationExtras if shouldKeepBackRoute is true or undefined
            updatedExtendedLink.navigationExtras.queryParams['backRoute'] = backRouteParam;
        } else if (updatedExtendedLink.shouldKeepBackRoute === false) {
            // Remove backRoute from navigationExtras if shouldKeepBackRoute is explicitly false
            delete updatedExtendedLink.navigationExtras.queryParams['backRoute'];
        }
        // Before returning, ensure return_url is URL encoded
        if (updatedExtendedLink.navigationExtras.queryParams['return_url']) {
            const currentReturnUrl = updatedExtendedLink.navigationExtras.queryParams['return_url'];
            const decodedUrl = decodeURIComponent(currentReturnUrl);
            // FORCE URL encode it
            updatedExtendedLink.navigationExtras.queryParams['return_url'] = encodeURIComponent(decodedUrl);
        }

        updatedExtendedLink.navigationExtras.queryParamsHandling = '';

        return updatedExtendedLink;
    }

    /**
     * Extracts query parameters from a URL string
     * @param link The URL string to extract parameters from
     * @returns An object containing the extracted query parameters
     */
    private extractQueryParamsFromLink(link: string): Params {
        const queryParams: Params = {};
        if (link && typeof link === 'string' && link.includes('?')) {
            const urlParts = link.split('?');
            if (urlParts.length > 1) {
                this.parseQueryString(urlParts[1], queryParams);
            }
        }
        return queryParams;
    }

    /**
     * Removes query parameters from a link object
     * @param link The link object to clean, came from Relation object
     * @returns The cleaned link object
     */
    private cleanQueryParamsFromRelationLink(link: string): string {
        // If no URL or no query parameters, return the original link
        if (!link?.includes('?')) {
            return link;
        }
        // Extract the base URL without query parameters
        return link.split('?')[0];
    }

    /**
     * Updates the link object with the cleaned URL by recursively traversing the object structure
     * @param link The original link object
     * @param cleanedUrl The cleaned URL string (without query parameters)
     * @returns The updated link object
     */
    public updateLinkWithCleanUrl(link: Link, cleanedUrl: string): Link {
        // Handle null/undefined
        if (!link) {
            return link;
        }

        // Clone the link object to avoid mutating the original
        const result = Array.isArray(link) ? [...link] : { ...link };

        // Process each property in the object
        Object.keys(result).forEach((key) => {
            const value = result[key];

            // Skip the criteria attribute as it contains metadata, not URLs
            if (key === 'criteria') {
                return;
            }

            // If value is string and looks like a URL, replace it with the cleaned URL
            if (typeof value === 'string') {
                result[key] = cleanedUrl;
            }
            // Recursively process nested objects and arrays
            else if (typeof value === 'object' && value !== null) {
                result[key] = this.updateLinkWithCleanUrl(value as any, cleanedUrl);
            }
        });

        return result as Link;
    }

    /**
     * Parses a query string and adds the parameters to the provided object
     * @param queryString The query string to parse
     * @param targetObj The object to add the parameters to
     */
    private parseQueryString(queryString: string, targetObj: Params): void {
        const params = queryString.split('&');
        for (const param of params) {
            const [key, value] = param.split('=');
            if (key) {
                targetObj[key] = value !== undefined ? decodeURIComponent(value) : '';
            }
        }
    }
}
